import { useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import {
  Box,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from "@mui/material";
import MediaPickerDialog from "./MediaPickerDialog";
import {
  getPreviewImageSrc,
  resolvePublicMediaUrl,
} from "../global/functions/imageHelper";
import { getMediaPickerTitle } from "../global/functions/mediaPickerHelper";
import { useTranslation } from "react-i18next";

const apiUrl = process.env.REACT_APP_MEDIA_URL;

const ImageInput = ({
  previewImage,
  setPreviewImage,
  image,
  imageId,
  row,
  colum,
  acceptVides = false,
  mediaTargetType = "",
  directUpload = false,
  defaultShared = false,
  mediaRestaurantId,
  standalone = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [shared, setShared] = useState(defaultShared);
  const ownerOnly = mediaTargetType === "Restaurant";
  const fileInputRef = useRef(null);
  const hasNewSelection = !!previewImage;
  const previewSrc = getPreviewImageSrc(previewImage, apiUrl);
  const imageLooksLikeId =
    typeof image === "string" && /^[a-fA-F0-9]{24}$/.test(image);
  const existingSrc = imageLooksLikeId
    ? ""
    : resolvePublicMediaUrl(image, apiUrl);
  const displaySrc = previewSrc || existingSrc;
  const initialMediaId =
    previewImage?.type === "existing"
      ? previewImage.mediaId
      : imageId || (imageLooksLikeId ? image : undefined);
  const initialMediaUrl =
    previewImage || imageLooksLikeId ? undefined : image;

  const handleClear = (event) => {
    event.stopPropagation();
    setPreviewImage(null);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setPreviewImage(file || null);
    event.target.value = "";
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "grid",
        justifyItems: "stretch",
        alignContent: "start",
        gap: 1,
        gridColumn: colum || "span 3",
        gridRow: row,
        width: "100%",
        maxWidth: 240,
      }}
    >
      <Box
        onClick={() =>
          directUpload ? fileInputRef.current?.click() : setPickerOpen(true)
        }
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "1",
          borderRadius: 2,
          border: "1px dashed",
          borderColor: displaySrc ? "transparent" : "divider",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.04)"
              : "#f7f7f7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          overflow: "hidden",
          "&:hover": {
            borderColor: "primary.main",
          },
        }}
      >
        {displaySrc ? (
          <img
            src={displaySrc}
            alt={t("fields.selectImage")}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        ) : (
          <Box sx={{ textAlign: "center", px: 2 }}>
            <AddPhotoAlternateOutlinedIcon
              sx={{ fontSize: 48, color: "primary.main", mb: 0.5 }}
            />
            <Typography variant="caption" color="text.secondary" display="block">
              {t("fields.selectImage")}
            </Typography>
          </Box>
        )}
        {hasNewSelection && (
          <IconButton
            onClick={handleClear}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
              backgroundColor: "white",
              width: 32,
              height: 32,
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            <CloseIcon sx={{ color: "error.main", fontSize: 20 }} />
          </IconButton>
        )}
      </Box>
      {!directUpload && !standalone && !ownerOnly && (
        <RadioGroup
          row
          value={shared ? "shared" : "exclusive"}
          onChange={(event) => setShared(event.target.value === "shared")}
        >
          <FormControlLabel
            value="shared"
            control={<Radio size="small" />}
            label={t("media.sharedOption")}
          />
          <FormControlLabel
            value="exclusive"
            control={<Radio size="small" />}
            label={t("media.notShared")}
          />
        </RadioGroup>
      )}
      {directUpload ? (
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptVides ? ".jpg,.jpeg,.png,.gif,.mp4" : ".jpg,.jpeg,.png"}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      ) : (
        <MediaPickerDialog
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={(selectedMedia) => setPreviewImage(selectedMedia)}
          targetType={mediaTargetType}
          title={getMediaPickerTitle(mediaTargetType)}
          initialMediaId={initialMediaId}
          initialMediaUrl={initialMediaUrl}
          acceptVideos={acceptVides}
          shared={ownerOnly ? false : standalone ? true : shared}
          restaurantIdOverride={
            ownerOnly ? mediaRestaurantId || "" : standalone ? "" : mediaRestaurantId
          }
          standalone={ownerOnly ? false : standalone}
        />
      )}
    </Box>
  );
};

export default ImageInput;
